import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { getBrokerFromPath } from '../config/brokerConfig';
import {
  transactionService,
  pendingRegistrationService,
  whitelistedAccountService,
} from '../services/firebaseService';

// Create context for broker-specific data
const BrokerDataContext = createContext(null);

// Provider component - loads data specific to the current broker
export function BrokerDataProvider({ children }) {
  const location = useLocation();
  const [brokerId, setBrokerId] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [pendingRegistrations, setPendingRegistrations] = useState([]);
  const [whitelistedAccounts, setWhitelistedAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Detect broker from URL path
  useEffect(() => {
    const detectedBrokerId = getBrokerFromPath(location.pathname);
    if (detectedBrokerId !== brokerId) {
      console.log(`[BrokerDataContext] Broker changed to: ${detectedBrokerId}`);
      setBrokerId(detectedBrokerId);
    }
  }, [location.pathname, brokerId]);

  // Subscribe to broker-specific data when brokerId changes
  useEffect(() => {
    if (!brokerId) return;

    console.log(`[BrokerDataContext] Setting up subscriptions for broker: ${brokerId}`);
    setIsLoading(true);

    let unsubTransactions;
    let unsubPendingRegistrations;
    let unsubWhitelistedAccounts;

    const setupSubscriptions = () => {
      // Subscribe to broker-specific transactions
      unsubTransactions = transactionService.subscribeToAll((data) => {
        console.log(`[BrokerDataContext] Loaded ${data.length} transactions for ${brokerId}`);
        setTransactions(data || []);
      }, brokerId);

      // Subscribe to broker-specific pending registrations
      unsubPendingRegistrations = pendingRegistrationService.subscribeToAll((data) => {
        console.log(`[BrokerDataContext] Loaded ${data.length} pending registrations for ${brokerId}`);
        setPendingRegistrations(data || []);
      }, brokerId);

      // Subscribe to broker-specific whitelisted accounts
      unsubWhitelistedAccounts = whitelistedAccountService.subscribeToAll((data) => {
        console.log(`[BrokerDataContext] Loaded ${data.length} whitelisted accounts for ${brokerId}`);
        setWhitelistedAccounts(data || []);
      }, brokerId);

      // Set loading to false after a short delay (subscriptions are async)
      setTimeout(() => setIsLoading(false), 1000);
    };

    setupSubscriptions();

    // Cleanup subscriptions when broker changes or component unmounts
    return () => {
      console.log(`[BrokerDataContext] Cleaning up subscriptions for broker: ${brokerId}`);
      if (unsubTransactions) unsubTransactions();
      if (unsubPendingRegistrations) unsubPendingRegistrations();
      if (unsubWhitelistedAccounts) unsubWhitelistedAccounts();
    };
  }, [brokerId]);

  // Computed values
  const transactionsNeedingAttention = transactions.filter(t =>
    t.status === 'pending_review' ||
    t.status === 'manual_review' ||
    t.status === 'pending' ||
    t.status === 'pending_payment' ||
    !t.status
  );

  const approvedTransactions = transactions.filter(t => t.status === 'approved');
  const completedTransactions = transactions.filter(t => t.status === 'completed');
  const rejectedTransactions = transactions.filter(t => t.status === 'rejected');

  // Calculate total volume
  const totalVolume = transactions.reduce((sum, t) => {
    const amount = parseFloat(t.amount) || 0;
    return sum + amount;
  }, 0);

  // Actions (with broker context)
  const updateTransaction = useCallback(async (transactionId, updates) => {
    if (!brokerId) return { success: false, error: 'No broker context' };
    return await transactionService.update(transactionId, updates, brokerId);
  }, [brokerId]);

  const approvePendingRegistration = useCallback(async (registrationId) => {
    if (!brokerId) return { success: false, error: 'No broker context' };
    return await pendingRegistrationService.approve(registrationId, brokerId);
  }, [brokerId]);

  const rejectPendingRegistration = useCallback(async (registrationId, reason) => {
    if (!brokerId) return { success: false, error: 'No broker context' };
    return await pendingRegistrationService.reject(registrationId, reason, brokerId);
  }, [brokerId]);

  const deleteWhitelistedAccount = useCallback(async (accountId) => {
    if (!brokerId) return { success: false, error: 'No broker context' };
    return await whitelistedAccountService.delete(accountId, brokerId);
  }, [brokerId]);

  const value = {
    // Current broker
    brokerId,

    // Raw data
    transactions,
    pendingRegistrations,
    whitelistedAccounts,

    // Loading state
    isLoading,

    // Computed values
    transactionsNeedingAttention,
    approvedTransactions,
    completedTransactions,
    rejectedTransactions,
    totalVolume,
    pendingRegistrationsCount: pendingRegistrations.length,
    whitelistedAccountsCount: whitelistedAccounts.filter(a => a.status === 'whitelisted').length,

    // Actions
    updateTransaction,
    approvePendingRegistration,
    rejectPendingRegistration,
    deleteWhitelistedAccount,
  };

  return (
    <BrokerDataContext.Provider value={value}>
      {children}
    </BrokerDataContext.Provider>
  );
}

// Custom hook to use broker data context
export function useBrokerData() {
  const context = useContext(BrokerDataContext);
  if (!context) {
    throw new Error('useBrokerData must be used within a BrokerDataProvider');
  }
  return context;
}

export default BrokerDataContext;
